export default {
  title: "Architect Keys",
  caption: "MCP bearer tokens for Claude Code & agents",
  menuTitle: "Manage keys",
  superadmin: {
    badge: "Superadmin",
    badgeTitle:
      "Global key — can call global tools (e.g. add to the MCP catalog) on top of tenant-scoped tools.",
    label: "Superadmin (global) key",
    hint: "Lets this key call global tools like adding entries to the MCP catalog, in addition to this tenant's tools. Super admin only.",
  },
  common: {
    copy: "Copy",
    // Emphasised word reused inside the "read and {suggest} changes" sentences.
    suggest: "suggest",
  },
  connect: {
    title: "Easiest way — connect with sign-in",
    subtitle:
      "No key to copy. Add a connector in your AI app, then sign in with your usual account.",
    step1:
      "In your AI app open {path} (Claude Desktop, ChatGPT, Le Chat, and more). Leave any “OAuth Client ID / Secret” fields blank.",
    step1Path: "Settings → Connectors → Add custom connector",
    chooseTools: "Choose which tools to connect:",
    pasteAddress: "Paste this address:",
    step3:
      "Click {action} and sign in. Approve once — the tools then appear in your app.",
    connectAction: "Connect",
    safety:
      "It can read and {suggest} changes — never apply them. You approve every change here in the app.",
  },
  keys: {
    titleOauth: "Access keys",
    titleNoOauth: "Connect your AI assistant",
    descOauth:
      "For command-line tools and scripts (Claude Code, cron, CI) — anywhere there's no browser to sign in. A key can read and {suggest} changes; never apply.",
    descNoOauth:
      "Create an access key to connect this hub's tools to Claude Desktop, ChatGPT, Gemini, Le Chat and more. A key can read and {suggest} changes — it can never apply them. You always approve here in the app.",
    newButton: "New access key",
    empty: {
      title: "No access keys yet",
      body: "Create a key and we'll show you exactly how to connect it to your AI app — Claude Desktop, ChatGPT, Gemini, Le Chat and more. The key is shown only once when created, so copy it before closing.",
    },
    table: {
      name: "Name",
      prefix: "Prefix",
      scopes: "Scopes",
      status: "Status",
      lastUsed: "Last used",
      expires: "Expires",
      actions: "Actions",
    },
    status: {
      active: "Active",
      expired: "Expired",
      revoked: "Revoked",
    },
  },
  relative: {
    never: "never",
    justNow: "just now",
    minutesAgo: "{n}m ago",
    hoursAgo: "{n}h ago",
    daysAgo: "{n}d ago",
  },
  actions: {
    rotate: "Rotate key",
    revoke: "Revoke key",
  },
  create: {
    title: "Create an access key",
    description:
      "The key is shown once on the next screen — copy it before closing.",
    usageLabel: "Where will you use this?",
    usagePlaceholder: "Pick the app or device…",
    usageHint: "We use this to label the key so you can recognise it later.",
    labelLabel: "Label",
    labelPlaceholder: "Jeff's laptop",
    labelHint: "Shown in this list and in audit rows. Edit it if you like.",
    ttlLabel: "Lifetime (days)",
    ttlHint: "Default 90. Maximum 365.",
    cancel: "Cancel",
    generate: "Generate",
  },
  usage: {
    myLaptop: "My laptop",
    other: "Other",
  },
  issued: {
    title: "Your key is ready — copy it now",
    description:
      "This is the only time the key is shown. Pick your app below and follow the steps. The hub only ever stores a one-way hash.",
    safeTitle: "Safe by design",
    safeBody:
      "This key can read and suggest changes — it can never apply them. You always approve here in the app.",
    whichApp: "Which app are you using?",
    whichTools: "Which tools?",
    download: "Download {filename}",
    rawSummary: "Just the access key (for any other client)",
    copyKey: "Copy key",
    closingTitle: "Closing this dialog hides the key for good.",
    closingBody:
      "The hub only ever stores a one-way hash. If you lose it, revoke and create a new one.",
    done: "Done",
  },
  servers: {
    aiemployee: "AI Employee tools",
    care: "Care tools",
    lms: "LMS tools",
    marketresearch: "Market Research tools",
  },
  clients: {
    "claude-desktop": {
      label: "Claude Desktop",
      where:
        "Claude menu → Settings → Developer → Edit Config. Paste this inside the file, then fully quit and reopen Claude Desktop.",
      note: "Needs Node.js installed (the bridge runs via npx). Newer Claude Desktop versions can also add a connector by URL with no key — see the URL tab.",
    },
    "cursor-vscode": {
      label: "Cursor / VS Code",
      where:
        "Cursor → ~/.cursor/mcp.json. VS Code → .vscode/mcp.json in your project. Merge this into the file.",
    },
    gemini: {
      label: "Gemini CLI",
      where: "Gemini CLI → ~/.gemini/settings.json. Merge this into the file.",
      note: "The Gemini web/mobile app connects via a sign-in step rather than a pasted key — see the URL tab.",
    },
    "claude-code": {
      label: "Claude Code",
      where: "Run this in your terminal.",
    },
    chatgpt: {
      label: "ChatGPT",
      where:
        "Settings → Connectors → Add custom connector (Developer mode) → paste this URL and sign in when prompted.",
      note: "ChatGPT connects by signing in, not by a pasted key. Use the URL; if it asks for an access key, paste the bearer from the bottom of this dialog.",
    },
    "le-chat": {
      label: "Le Chat (Mistral)",
      where:
        "Le Chat → Connectors → Add connector → paste this URL and sign in when prompted.",
      note: "Le Chat connects by signing in, not by a pasted key. Use the URL; if it asks for an access key, paste the bearer from the bottom of this dialog.",
    },
    other: {
      label: "Other",
      where:
        "Most MCP clients accept a server URL plus an Authorization header. Use this config, or the raw URL + bearer below.",
    },
  },
  dialogs: {
    rotateTitle: "Rotate {name}?",
    rotateMessage:
      "Mints a replacement key and gives this one a 14-day grace expiry so you can swap configs without downtime. The new bearer is shown once on the next screen.",
    revokeTitle: "Revoke {name}?",
    revokeMessage:
      "This is synchronous — the next MCP call from this bearer will fail. The row stays for audit but the key is dead immediately.",
  },
  toasts: {
    addressCopied: "Address copied — paste it into your AI app.",
    keyCopiedConfig: "Copied — paste into your MCP config now.",
    snippetCopied: "Copied — paste it into your AI app.",
    downloaded: "Downloaded {filename} — open it in your AI app's config.",
    revoked: "Revoked.",
  },
};
